import { defineResource } from '@tnn/core'
import { z } from 'zod'

/**
 * `counter`: takes parcels from customers; `dc`: sorts and delivers in its area; `both`;
 * `hq`: head office, which may take the freight of any waybill (its receipts are its own series)
 */
export const branchTypes = ['counter', 'dc', 'both', 'hq'] as const
export const branchStatuses = ['active', 'inactive'] as const

/**
 * company.branch: a place of the company. Every app works from it — waybills start and travel between branches, people
 * belong to one and clock in there, cash is kept there — so it is the company's, not one app's. Everyone may read
 * branches (`everyonePermissions`); its join code only the people who post or change it, and that branch's own people.
 */
export const branch = defineResource({
  app: 'company',
  name: 'branch',
  path: 'branches',
  schema: z.object({
    code: z
      .string()
      .trim()
      .toUpperCase()
      .min(2)
      .max(12)
      .regex(/^[A-Z0-9-]+$/),
    name: z.string().trim().min(1).max(100),
    type: z.enum(branchTypes).default('counter'),
    phone: z.string().trim().max(50).nullish(),
    address: z.string().max(500).nullish(),
    /** Where this branch delivers: a province ("นครศรีธรรมราช") or one district of it ("นครศรีธรรมราช/ทุ่งสง") */
    coverage: z.array(z.string().trim().min(1)).default([]),
    /** Places inside its coverage it does not deliver to ("นครศรีธรรมราช/ขนอม", or a subdistrict of it): see `branchForArea` */
    excluded: z.array(z.string().trim().min(1)).default([]),
    /** Branches its trucks run to directly; parcels travel along these links (see `planRoute`) */
    connectsTo: z.array(z.string()).default([]),
    status: z.enum(branchStatuses).default('active'),
    notes: z.string().max(1000).nullish(),
    /** Where the branch is: a scan farther than `clockRadius` metres is flagged for the lead (not refused) */
    latitude: z.number().min(-90).max(90).nullish(),
    longitude: z.number().min(-180).max(180).nullish(),
    clockRadius: z.number().int().min(50).max(5000).default(300),
  }),
  fields: {
    code: { search: true, list: { width: 110, priority: 1 }, form: { span: 3 }, import: { aliases: ['รหัสสาขา'] } },
    name: { search: true, list: { priority: 1 }, form: { span: 6 }, import: { aliases: ['ชื่อสาขา'] } },
    type: { filter: true, list: { width: 160, priority: 1 }, form: { span: 3 } },
    phone: { type: 'phone', list: { priority: 3 }, form: { span: 4 } },
    address: { type: 'textarea', list: false, form: { span: 8 } },
    coverage: { type: 'tags', list: { priority: 2 }, form: { span: 12, section: 'network', custom: true } },
    excluded: { type: 'tags', list: { priority: 3, hidden: true }, form: { span: 12, section: 'network', custom: true } },
    connectsTo: {
      type: 'multiselect',
      ref: { resource: 'company.branch', where: { status: 'active' } },
      list: { priority: 3 },
      form: { span: 12, section: 'network' },
    },
    status: { filter: true, tones: { active: 'success', inactive: 'neutral' }, list: { width: 120, priority: 1 }, form: { span: 4 } },
    notes: { type: 'textarea', form: { span: 12, section: 'network' } },
    /** Shown at the branch: someone signing up types it, so only people who are there can ask (server-made, can be changed) */
    joinCode: { readOnly: true, list: { priority: 3, hidden: true } },
    latitude: { type: 'number', list: false, form: { span: 4, section: 'clock' } },
    longitude: { type: 'number', list: false, form: { span: 4, section: 'clock' } },
    clockRadius: { type: 'integer', list: false, form: { span: 4, section: 'clock' } },
  },
  title: 'name',
  columns: ['code', 'name', 'type', 'coverage', 'excluded', 'status', 'phone', 'connectsTo'],
  sections: ['main', 'network', 'clock'],
  defaultSort: 'code',
  importKey: 'code',
  // `rotateCode`: give the branch a new join code (the old one stops working); `clockFor`: at the
  // counter, clock in or out someone without a phone (the branch's own people only)
  actions: ['read', 'create', 'update', 'delete', 'import', 'export', 'rotateCode', 'clockFor'],
  card: { title: 'name', subtitle: 'code', meta: ['type'], status: 'status' },
})
